import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Alternador, Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { rotuloModulo } from '../../nucleo/rotulos';
import { carregarMais } from '../../nucleo/foco';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { rotuloAutor } from './Historico';
import type { EntradaAuditoria } from './tipos';

/** Um campo configurável da tela (`GET /configuracoes/campos-telas`). */
interface CampoTela {
  chave: string;
  rotulo: string;
  travado: boolean;
  pode_esconder: boolean;
  pode_obrigar: boolean;
  visivel: boolean;
  obrigatorio: boolean;
}

/** Uma tela de cadastro declarada por um módulo instalado (`telas_campos` no `module.json`). */
export interface TelaCampos {
  modulo: string;
  modulo_nome: string;
  tela: string;
  rotulo: string;
  definido: boolean;
  campos: CampoTela[];
}

type Estado = Record<string, { visivel: boolean; obrigatorio: boolean }>;

const PERMISSAO = 'configuracoes.campos_telas';

/** Texto do módulo dono (namespace dele): `<slug>:camposTela.<nome da tela>.titulo|campos.<chave>`, com o rótulo do servidor de reserva. */
function textoDaTela(t: (k: string, o?: Record<string, unknown>) => string, tela: string, padrao: string, campo?: string) {
  const ponto = tela.indexOf('.');
  const base = `${tela.slice(0, ponto)}:camposTela.${tela.slice(ponto + 1)}`;
  return t(campo ? `${base}.campos.${campo}` : `${base}.titulo`, { defaultValue: padrao });
}

/**
 * Lote de 19/10 (item 9): Configurações > Campos das telas. Escolhe a tela de cadastro e, para cada campo nativo, se aparece e
 * se é obrigatório no workspace. Campo travado (entra em regra, relatório, integração ou cálculo) aparece com o cadeado e não
 * muda. A escolha vale nos formulários, nas fichas e no modelo de importação; o servidor recusa o obrigatório vazio.
 */
export function CamposTelas() {
  const { t } = useTranslation('configuracoes');
  const s = useSessao();
  if (!s.pode(PERMISSAO)) {
    return (
      <Cartao titulo={t('camposTelas.titulo')}>
        <p className="text-sm text-tinta-3">{t('camposTelas.semPermissao')}</p>
      </Cartao>
    );
  }
  return <Lista />;
}

function Lista() {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const s = useSessao();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['configuracoes', 'campos-telas'], queryFn: () => get<{ itens: TelaCampos[] }>('/configuracoes/campos-telas') });
  const itens = q.data?.itens ?? [];
  // A tela da URL (atalho "Configurar campos" do formulário) só vale se for uma das listadas; senão, a primeira.
  const pedida = params.get('tela');
  const tela = itens.find((x) => x.tela === pedida) ?? itens[0] ?? null;
  return (
    <div className="grid min-w-0 gap-4 xl:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4">
        <Cartao titulo={t('camposTelas.titulo')} semPadding>
          {q.isPending ? (
            <Carregando />
          ) : q.isError ? (
            <Erro erro={q.error} aoTentar={() => q.refetch()} />
          ) : !tela ? (
            <Vazio texto={t('camposTelas.vazio')} />
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-4 border-b border-borda px-4 py-3">
                <div className="w-full sm:w-80">
                  <Rotulo id="campos-telas-tela" rotulo={t('camposTelas.tela')}>
                    <Selecao
                      id="campos-telas-tela"
                      opcoes={itens.map((x) => ({ valor: x.tela, rotulo: `${rotuloModulo(x.modulo, x.modulo_nome)} > ${textoDaTela(tg, x.tela, x.rotulo)}` }))}
                      value={tela.tela}
                      onChange={(ev) => setParams((p) => ({ ...Object.fromEntries(p), tela: ev.target.value }), { replace: true })}
                    />
                  </Rotulo>
                </div>
                <p className="min-w-0 flex-1 basis-60 text-sm text-tinta-3">{t('camposTelas.ajuda')}</p>
              </div>
              <FormTela key={`${tela.tela}:${JSON.stringify(tela.campos)}`} tela={tela} podeGravar={podeNoWorkspace(s, PERMISSAO)} />
            </>
          )}
        </Cartao>
      </div>
      {s.pode('configuracoes.ver') && <HistoricoCamposTelas telas={itens} />}
    </div>
  );
}

function FormTela({ tela, podeGravar }: { tela: TelaCampos; podeGravar: boolean }) {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const inicial: Estado = Object.fromEntries(tela.campos.map((c) => [c.chave, { visivel: c.visivel, obrigatorio: c.obrigatorio }]));
  const [estado, setEstado] = useState<Estado>(inicial);
  useEffect(() => setEstado(inicial), [tela.tela]); // eslint-disable-line react-hooks/exhaustive-deps
  const mudou = tela.campos.some((c) => estado[c.chave]!.visivel !== c.visivel || estado[c.chave]!.obrigatorio !== c.obrigatorio);
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/campos-telas', { tela: tela.tela, campos: Object.fromEntries(tela.campos.filter((c) => !c.travado).map((c) => [c.chave, estado[c.chave]!])) }),
    onSuccess: async () => {
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'campos-telas'] });
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] });
      toast.sucesso(t('camposTelas.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const mudar = (chave: string, v: Partial<Estado[string]>) =>
    setEstado((x) => {
      const novo = { ...x[chave]!, ...v };
      // Escondido nunca é obrigatório.
      if (!novo.visivel) novo.obrigatorio = false;
      return { ...x, [chave]: novo };
    });
  return (
    <div className="flex min-w-0 flex-col">
      <ul className="divide-y divide-borda">
        {tela.campos.map((c) => {
          const e = estado[c.chave]!;
          const rotulo = textoDaTela(tg, tela.tela, c.rotulo, c.chave);
          return (
            <li key={c.chave} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <span className="flex min-w-0 flex-1 basis-44 items-center gap-2">
                <span className="min-w-0 truncate font-medium" title={rotulo}>
                  {rotulo}
                </span>
                {c.travado && (
                  <Selo>
                    <span className="inline-flex items-center gap-1" title={t('camposTelas.travadoDica')}>
                      <Lock className="size-3" aria-hidden />
                      {t('camposTelas.travado')}
                    </span>
                  </Selo>
                )}
              </span>
              <label className="flex items-center gap-2 text-sm text-tinta-2">
                <Alternador marcado={e.visivel} rotulo={`${t('camposTelas.aparece')}: ${rotulo}`} desabilitado={!podeGravar || !c.pode_esconder} aoMudar={(v) => mudar(c.chave, { visivel: v })} />
                {t('camposTelas.aparece')}
              </label>
              <label className="flex items-center gap-2 text-sm text-tinta-2">
                <Alternador marcado={e.obrigatorio} rotulo={`${t('camposTelas.obrigatorio')}: ${rotulo}`} desabilitado={!podeGravar || !c.pode_obrigar || !e.visivel} aoMudar={(v) => mudar(c.chave, { obrigatorio: v })} />
                {t('camposTelas.obrigatorio')}
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-borda px-4 py-3">
        {!podeGravar && <p className="min-w-0 flex-1 text-sm text-tinta-3">{t('camposTelas.soQuemVeTodas')}</p>}
        <Botao variante="fantasma" disabled={!mudou || salvar.isPending} onClick={() => setEstado(inicial)}>
          {t('camposTelas.descartar')}
        </Botao>
        {/* Secundário: a aba de Configurações não tem primário (como o Modo de operação). */}
        <Botao disabled={!mudou || !podeGravar} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
          {t('camposTelas.salvar')}
        </Botao>
      </div>
    </div>
  );
}

interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/** Mudanças nos campos das telas (auditoria `configuracoes.campos_tela`): a tela e, por campo, como ficou. */
function HistoricoCamposTelas({ telas }: { telas: TelaCampos[] }) {
  const { t } = useTranslation('configuracoes');
  const { t: tg } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: ['configuracoes', 'historico', 'configuracoes.campos_tela'],
    queryFn: ({ pageParam }) => get<Pagina<EntradaAuditoria>>('/configuracoes/historico', { entidade: 'configuracoes.campos_tela', limite: 15, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  const texto = (i: EntradaAuditoria) => {
    const a = (i.antes ?? {}) as { tela?: string; campos?: Estado };
    const d = (i.depois ?? {}) as { tela?: string; campos?: Estado };
    const nomeTela = d.tela ?? a.tela;
    if (!nomeTela) return t('historico.sem');
    const declarada = telas.find((x) => x.tela === nomeTela);
    const chaves = [...new Set([...Object.keys(a.campos ?? {}), ...Object.keys(d.campos ?? {})])].sort();
    const partes = chaves.map((k) => {
      const v = d.campos?.[k];
      const como = !v ? t('camposTelas.padrao') : !v.visivel ? t('camposTelas.escondido') : v.obrigatorio ? t('camposTelas.obrigatorio') : t('camposTelas.padrao');
      return `${textoDaTela(tg, nomeTela, declarada?.campos.find((c) => c.chave === k)?.rotulo ?? k, k)}: ${como}`;
    });
    return t('camposTelas.troca', { tela: textoDaTela(tg, nomeTela, declarada?.rotulo ?? nomeTela), lista: partes.join('; ') || t('camposTelas.padrao') });
  };
  return (
    <Cartao titulo={t('camposTelas.historico')}>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('historico.vazio')}</p>
      ) : (
        <>
          <ul className="divide-y divide-borda">
            {itens.map((i) => (
              <li key={i.id} className="flex min-w-0 flex-col gap-1 py-2.5 text-sm">
                <span className="font-medium break-words">{texto(i)}</span>
                <span className="text-xs text-tinta-3">
                  {t('historico.por', { nome: rotuloAutor(i, t) })} · {dataHora(i.created_at)}
                </span>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-borda pt-3">
              <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                {tg('comum.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}
